/**
 * ContentROIDashboard
 *
 * Dashboard for tracking the ROI of AI-optimized content changes.
 * Shows value created, position improvements, LLM evaluations, and timeline.
 *
 * GitHub Issue: #369 - Content ROI Analytics
 * Phase 4: Dashboard UI
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  TrendingUp,
  TrendingDown,
  AlertCircle,
  DollarSign,
  Bot,
  Target,
  Calendar,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  Trophy,
  BarChart3,
  Sparkles,
  Clock,
  RefreshCw,
  Search,
  Plus,
  Camera,
  PlayCircle,
  Loader2
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from 'recharts';
import {
  fetchSiteDashboard,
  fetchPageDashboard,
  fetchCTRCurve,
  createSnapshot,
  triggerEvaluation,
  trackKeywords,
  fetchPageGoogleMetrics,
  fetchGoogleROIComparison,
  checkGoogleIntegrationStatus,
  syncFromGSCOrFallback,
  type SiteDashboardData,
  type PageDashboardData,
  type CTRCurve,
  type GoogleMetrics,
  type GoogleROIComparison
} from '@/services/contentROIService';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

export default function ContentROIDashboard() {
  const { selectedSite } = useSite();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const pageId = searchParams.get('page');

  const [loading, setLoading] = useState(true);
  const [siteData, setSiteData] = useState<SiteDashboardData | null>(null);
  const [pageData, setPageData] = useState<PageDashboardData | null>(null);
  const [ctrCurve, setCtrCurve] = useState<CTRCurve | null>(null);
  const [activeTab, setActiveTab] = useState(pageId ? 'page' : 'overview');

  // Google integration state
  const [googleIntegration, setGoogleIntegration] = useState<{
    gscConnected: boolean;
    ga4Connected: boolean;
  }>({ gscConnected: false, ga4Connected: false });
  const [googleMetrics, setGoogleMetrics] = useState<GoogleMetrics | null>(null);
  const [googleComparison, setGoogleComparison] = useState<GoogleROIComparison | null>(null);

  // Dialog states
  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
  const [evalDialogOpen, setEvalDialogOpen] = useState(false);
  const [keywordDialogOpen, setKeywordDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Form states
  const [selectedPageId, setSelectedPageId] = useState<string>('');
  const [snapshotType, setSnapshotType] = useState<'before' | 'after'>('before');
  const [evalProviders, setEvalProviders] = useState<string[]>(['openai']);
  const [keywordsText, setKeywordsText] = useState('');

  // Pages list for selection
  const [sitePages, setSitePages] = useState<Array<{ id: string; url: string; title: string }>>([]);

  useEffect(() => {
    if (selectedSite) {
      loadData();
    }
  }, [selectedSite, pageId]);

  const loadData = async () => {
    if (!selectedSite) return;
    setLoading(true);
    try {
      // Always load site data
      const site = await fetchSiteDashboard(selectedSite.id);
      setSiteData(site);

      // Load CTR curve
      const ctr = await fetchCTRCurve();
      setCtrCurve(ctr);

      // Check Google integration status
      const googleStatus = await checkGoogleIntegrationStatus(selectedSite.id);
      setGoogleIntegration(googleStatus);

      // Load page data if page ID is provided
      if (pageId) {
        const page = await fetchPageDashboard(pageId);
        setPageData(page);
        setActiveTab('page');

        // Load Google metrics for this page if integration is connected
        if (googleStatus.gscConnected || googleStatus.ga4Connected) {
          const metrics = await fetchPageGoogleMetrics(selectedSite.id, page.summary.page.url);
          setGoogleMetrics(metrics);

          // Load comparison if we have a deployment date
          if (page.summary.lastDeployment) {
            const comparison = await fetchGoogleROIComparison(
              selectedSite.id,
              page.summary.page.url,
              page.summary.lastDeployment
            );
            setGoogleComparison(comparison);
          }
        }
      }

      // Load site pages for action dialogs
      await loadSitePages();
    } catch (error) {
      console.error('Failed to load Content ROI dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSitePages = async () => {
    if (!selectedSite) return;
    try {
      // Use the Content ROI pages endpoint which fetches from knowledge_base_entries
      const response = await authFetch(`${API_URL}/content-roi/pages/${selectedSite.id}?limit=100`);
      const data = await response.json();
      if (response.ok && data.pages) {
        setSitePages(data.pages.map((p: { id: string; url: string; title?: string; brand?: string }) => ({
          id: p.id,
          url: p.url,
          title: p.title || p.url,
          brand: p.brand
        })));
      }
    } catch (error) {
      console.error('Failed to load site pages:', error);
    }
  };

  // Action handlers
  const handleCreateSnapshot = async () => {
    if (!selectedPageId) {
      setActionError('Please select a page');
      return;
    }
    setActionLoading(true);
    setActionError(null);
    try {
      await createSnapshot(selectedPageId, snapshotType);
      setActionSuccess(`Created ${snapshotType} snapshot successfully`);
      setSnapshotDialogOpen(false);
      await loadData();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Failed to create snapshot');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunEvaluation = async () => {
    if (!selectedPageId) {
      setActionError('Please select a page');
      return;
    }
    setActionLoading(true);
    setActionError(null);
    try {
      const result = await triggerEvaluation(selectedPageId, evalProviders);
      if (result.errors && result.errors.length > 0) {
        setActionError(`Evaluation completed with errors: ${result.errors.map(e => e.error).join(', ')}`);
      } else {
        setActionSuccess(`LLM evaluation completed: ${result.evaluations.length} provider(s)`);
      }
      setEvalDialogOpen(false);
      await loadData();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Failed to run evaluation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTrackKeywords = async () => {
    if (!selectedPageId) {
      setActionError('Please select a page');
      return;
    }
    if (!keywordsText.trim()) {
      setActionError('Please enter keywords');
      return;
    }
    setActionLoading(true);
    setActionError(null);
    try {
      // Parse keywords - format: "keyword, position" per line
      const keywords = keywordsText.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => {
          const parts = line.split(',');
          return {
            keyword: parts[0].trim(),
            position: parseInt(parts[1]?.trim() || '50', 10)
          };
        });

      if (keywords.length === 0) {
        setActionError('No valid keywords found');
        return;
      }

      await trackKeywords(selectedPageId, keywords);
      setActionSuccess(`Tracked ${keywords.length} keyword(s) successfully`);
      setKeywordDialogOpen(false);
      setKeywordsText('');
      await loadData();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Failed to track keywords');
    } finally {
      setActionLoading(false);
    }
  };

  // Clear messages after delay
  useEffect(() => {
    if (actionSuccess) {
      const timer = setTimeout(() => setActionSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [actionSuccess]);

  useEffect(() => {
    if (actionError) {
      const timer = setTimeout(() => setActionError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [actionError]);

  const formatCurrency = (value: number | string | null) => {
    if (value === null) return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const formatPercent = (value: number | string | null) => {
    if (value === null) return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `${num >= 0 ? '+' : ''}${num.toFixed(1)}%`;
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'deployment':
        return <Sparkles className="h-4 w-4 text-blue-500" />;
      case 'serp_change':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'bot_crawl':
        return <Bot className="h-4 w-4 text-orange-500" />;
      case 'competitor_overtaken':
        return <Trophy className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getEventBadgeColor = (eventType: string) => {
    switch (eventType) {
      case 'deployment':
        return 'bg-blue-100 text-blue-800';
      case 'serp_change':
        return 'bg-green-100 text-green-800';
      case 'bot_crawl':
        return 'bg-orange-100 text-orange-800';
      case 'competitor_overtaken':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site to view the Content ROI dashboard.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Content ROI Analytics</h1>
          <p className="text-muted-foreground">
            Track the impact of AI-optimized content on rankings and revenue
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Status Messages */}
      {actionSuccess && (
        <Alert className="bg-green-50 border-green-200">
          <AlertDescription className="text-green-800">{actionSuccess}</AlertDescription>
        </Alert>
      )}
      {actionError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      {/* Action Buttons */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Manual Actions</CardTitle>
          <CardDescription>Populate data by creating snapshots, running evaluations, and tracking keywords</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {/* Create Snapshot Dialog */}
            <Dialog open={snapshotDialogOpen} onOpenChange={setSnapshotDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Camera className="h-4 w-4 mr-2" />
                  Create Snapshot
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Content Snapshot</DialogTitle>
                  <DialogDescription>
                    Take a snapshot of a page's content state. Use "before" before making changes, and "after" once content is deployed.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="snapshot-page">Select Page</Label>
                    <Select value={selectedPageId} onValueChange={setSelectedPageId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a page" />
                      </SelectTrigger>
                      <SelectContent>
                        {sitePages.map((page) => (
                          <SelectItem key={page.id} value={page.id}>
                            {page.title || page.url}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="snapshot-type">Snapshot Type</Label>
                    <Select value={snapshotType} onValueChange={(v) => setSnapshotType(v as 'before' | 'after')}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="before">Before (baseline)</SelectItem>
                        <SelectItem value="after">After (post-deployment)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSnapshotDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateSnapshot} disabled={actionLoading}>
                    {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Create Snapshot
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Run LLM Evaluation Dialog */}
            <Dialog open={evalDialogOpen} onOpenChange={setEvalDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Bot className="h-4 w-4 mr-2" />
                  Run LLM Evaluation
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Run LLM Content Evaluation</DialogTitle>
                  <DialogDescription>
                    Have AI models evaluate the page content quality. Requires an existing snapshot.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="eval-page">Select Page</Label>
                    <Select value={selectedPageId} onValueChange={setSelectedPageId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a page" />
                      </SelectTrigger>
                      <SelectContent>
                        {sitePages.map((page) => (
                          <SelectItem key={page.id} value={page.id}>
                            {page.title || page.url}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>LLM Providers</Label>
                    <div className="flex flex-wrap gap-2">
                      {['openai', 'gemini'].map((provider) => (
                        <Button
                          key={provider}
                          variant={evalProviders.includes(provider) ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            if (evalProviders.includes(provider)) {
                              setEvalProviders(evalProviders.filter(p => p !== provider));
                            } else {
                              setEvalProviders([...evalProviders, provider]);
                            }
                          }}
                        >
                          {provider === 'openai' ? 'OpenAI GPT-4o' : 'Google Gemini'}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEvalDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleRunEvaluation} disabled={actionLoading || evalProviders.length === 0}>
                    {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Run Evaluation
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Sync from GSC Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!selectedSite) return;
                setActionLoading(true);
                setActionError(null);
                try {
                  const result = await syncFromGSCOrFallback(selectedSite.id);
                  setActionSuccess(
                    result.source === 'gsc'
                      ? `Synced ${result.synced} keywords from Google Search Console`
                      : `Synced ${result.synced} keywords from SERP tracking (GSC not connected)`
                  );
                  await loadData();
                } catch (error) {
                  setActionError(error instanceof Error ? error.message : 'Failed to sync');
                } finally {
                  setActionLoading(false);
                }
              }}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync Positions {googleIntegration.gscConnected ? '(GSC)' : '(SERP)'}
            </Button>

            {/* Track Keywords Dialog */}
            <Dialog open={keywordDialogOpen} onOpenChange={setKeywordDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Target className="h-4 w-4 mr-2" />
                  Track Keywords
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Track Keywords</DialogTitle>
                  <DialogDescription>
                    Add keywords to track for a page. Enter one keyword per line with its current position.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="keyword-page">Select Page</Label>
                    <Select value={selectedPageId} onValueChange={setSelectedPageId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a page" />
                      </SelectTrigger>
                      <SelectContent>
                        {sitePages.map((page) => (
                          <SelectItem key={page.id} value={page.id}>
                            {page.title || page.url}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="keywords">Keywords (one per line)</Label>
                    <Textarea
                      id="keywords"
                      placeholder="keyword, position
e.g.:
motor doga, 8
doga actuators, 12
linear actuator 24v, 15"
                      value={keywordsText}
                      onChange={(e) => setKeywordsText(e.target.value)}
                      rows={6}
                    />
                    <p className="text-xs text-muted-foreground">
                      Format: keyword, position (position is optional, defaults to 50)
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setKeywordDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleTrackKeywords} disabled={actionLoading}>
                    {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Track Keywords
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Site Overview</TabsTrigger>
          <TabsTrigger value="pages">Top Pages</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          {pageData && <TabsTrigger value="page">Page Details</TabsTrigger>}
        </TabsList>

        {/* Site Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Value Created</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(siteData?.summary.totalValueCreatedEur || 0)}/mo
                </div>
                <p className="text-xs text-muted-foreground">
                  Monthly opportunity value from improvements
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Keywords in Top 10</CardTitle>
                <Target className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {siteData?.summary.keywordsInTop10 || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  Keywords ranking in first page
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">LLM Score</CardTitle>
                <Sparkles className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold">
                    {siteData?.summary.avgLlmScoreBefore || '-'}
                  </span>
                  {siteData?.summary.avgLlmScoreAfter && (
                    <>
                      <ArrowUp className="h-4 w-4 text-green-500" />
                      <span className="text-lg text-green-600">
                        {siteData.summary.avgLlmScoreAfter}
                      </span>
                    </>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Average content quality score (1-10)
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pages Tracked</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {siteData?.summary.pagesTracked || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  {siteData?.summary.totalDeployments || 0} total deployments
                </p>
              </CardContent>
            </Card>
          </div>

          {/* CTR Curve Visualization */}
          {ctrCurve && (
            <Card>
              <CardHeader>
                <CardTitle>CTR by Position</CardTitle>
                <CardDescription>
                  Click-through rate model used for value calculations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={Object.entries(ctrCurve.positions).map(([pos, ctr]) => ({
                        position: `#${pos}`,
                        ctr: ctr * 100
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="position" />
                      <YAxis
                        tickFormatter={(value) => `${value}%`}
                        domain={[0, 35]}
                      />
                      <Tooltip
                        formatter={(value: number) => [`${value.toFixed(1)}%`, 'CTR']}
                      />
                      <Bar dataKey="ctr" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Source: {ctrCurve.source}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Google Integration Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google Data Status
              </CardTitle>
              <CardDescription>
                Real performance data from Google Search Console and Analytics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg border ${googleIntegration.gscConnected ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Search className="h-4 w-4" />
                    <span className="font-medium">Search Console</span>
                    <Badge variant={googleIntegration.gscConnected ? 'default' : 'secondary'} className="ml-auto">
                      {googleIntegration.gscConnected ? 'Connected' : 'Not Connected'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {googleIntegration.gscConnected
                      ? 'Real position data enhances value calculations'
                      : 'Connect GSC for accurate position tracking'}
                  </p>
                </div>
                <div className={`p-4 rounded-lg border ${googleIntegration.ga4Connected ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="h-4 w-4" />
                    <span className="font-medium">Analytics 4</span>
                    <Badge variant={googleIntegration.ga4Connected ? 'default' : 'secondary'} className="ml-auto">
                      {googleIntegration.ga4Connected ? 'Connected' : 'Not Connected'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {googleIntegration.ga4Connected
                      ? 'Engagement metrics measure content impact'
                      : 'Connect GA4 for engagement tracking'}
                  </p>
                </div>
              </div>
              {!googleIntegration.gscConnected && !googleIntegration.ga4Connected && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Tip:</strong> Connect your Google accounts in{' '}
                    <a href="/settings/integrations" className="underline hover:text-blue-600">
                      Settings → Google Integrations
                    </a>{' '}
                    to see real position data and engagement metrics.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest ROI events across all pages</CardDescription>
            </CardHeader>
            <CardContent>
              {siteData?.recentTimeline && siteData.recentTimeline.length > 0 ? (
                <div className="space-y-4">
                  {siteData.recentTimeline.slice(0, 5).map((event) => (
                    <div
                      key={event.id}
                      className="flex items-start gap-4 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer"
                      onClick={() => navigate(`?page=${event.page_id}`)}
                    >
                      {getEventIcon(event.event_type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge className={getEventBadgeColor(event.event_type)}>
                            {event.event_type.replace('_', ' ')}
                          </Badge>
                          {event.impact_value_eur && parseFloat(event.impact_value_eur) !== 0 && (
                            <span className={`text-sm font-medium ${
                              parseFloat(event.impact_value_eur) > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatCurrency(event.impact_value_eur)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm mt-1 truncate">{event.page_title || event.page_url}</p>
                        {event.event_data.keyword && (
                          <p className="text-xs text-muted-foreground">
                            "{event.event_data.keyword}" - Position {event.event_data.oldPosition} → {event.event_data.newPosition}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(event.event_timestamp).toLocaleString()}
                        </p>
                      </div>
                      <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No events recorded yet. Create a snapshot to start tracking.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Top Pages Tab */}
        <TabsContent value="pages" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Top Performing Pages</CardTitle>
              <CardDescription>
                Pages with highest value created from content improvements
              </CardDescription>
            </CardHeader>
            <CardContent>
              {siteData?.topPages && siteData.topPages.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Page</TableHead>
                      <TableHead className="text-right">Value Created</TableHead>
                      <TableHead className="w-20"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {siteData.topPages.map((page) => (
                      <TableRow
                        key={page.id}
                        className="cursor-pointer hover:bg-accent/50"
                        onClick={() => navigate(`?page=${page.id}`)}
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium truncate max-w-md">{page.title}</p>
                            <p className="text-xs text-muted-foreground truncate max-w-md">
                              {page.url}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-green-600 font-medium">
                            {formatCurrency(page.total_value_eur)}/mo
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No pages with tracked value yet. Track keywords to see value.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>ROI Timeline</CardTitle>
              <CardDescription>
                Complete timeline of content changes and their impact
              </CardDescription>
            </CardHeader>
            <CardContent>
              {siteData?.recentTimeline && siteData.recentTimeline.length > 0 ? (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
                  <div className="space-y-6">
                    {siteData.recentTimeline.map((event, index) => (
                      <div key={event.id} className="relative pl-10">
                        <div className="absolute left-0 top-1 w-8 h-8 rounded-full bg-background border-2 border-border flex items-center justify-center">
                          {getEventIcon(event.event_type)}
                        </div>
                        <Card className="hover:shadow-md transition-shadow">
                          <CardContent className="pt-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <Badge className={getEventBadgeColor(event.event_type)}>
                                  {event.event_type.replace('_', ' ')}
                                </Badge>
                                <p className="font-medium mt-2">{event.page_title}</p>
                                <p className="text-sm text-muted-foreground truncate max-w-md">
                                  {event.page_url}
                                </p>
                                {event.event_data.keyword && (
                                  <p className="text-sm mt-1">
                                    <Search className="inline h-3 w-3 mr-1" />
                                    "{event.event_data.keyword}" - Position {event.event_data.oldPosition} → {event.event_data.newPosition}
                                    {event.event_data.positionDelta && (
                                      <span className="text-green-600 ml-2">
                                        (+{event.event_data.positionDelta} positions)
                                      </span>
                                    )}
                                  </p>
                                )}
                                {event.event_data.wordCount && (
                                  <p className="text-sm mt-1">
                                    Word count: {event.event_data.wordCount}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                {event.impact_value_eur && parseFloat(event.impact_value_eur) !== 0 && (
                                  <p className={`font-bold ${
                                    parseFloat(event.impact_value_eur) > 0 ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {formatCurrency(event.impact_value_eur)}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  {new Date(event.event_timestamp).toLocaleDateString()}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(event.event_timestamp).toLocaleTimeString()}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No timeline events yet.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Page Details Tab */}
        {pageData && (
          <TabsContent value="page" className="space-y-6">
            {/* Page Header */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {pageData.summary.page.title}
                </CardTitle>
                <CardDescription className="flex items-center gap-2">
                  <a
                    href={pageData.summary.page.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-1"
                  >
                    {pageData.summary.page.url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Page KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Monthly Value</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(pageData.summary.valueCreated.totalEurPerMonth)}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Keywords Tracked</CardTitle>
                  <Search className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {pageData.summary.positionImprovements.keywordsTracked}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {pageData.summary.positionImprovements.keywordsInTop10} in top 10
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Position Change</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    +{pageData.summary.positionImprovements.avgImprovement}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Competitors Beaten</CardTitle>
                  <Trophy className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {pageData.summary.competitorsOvertaken}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Google Metrics Section */}
            {(googleIntegration.gscConnected || googleIntegration.ga4Connected) && googleMetrics?.hasGoogleData && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Google Performance Data
                  </CardTitle>
                  <CardDescription>
                    Real metrics from Google Search Console and Analytics
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* GSC Metrics */}
                    {googleMetrics.gsc && (
                      <div className="space-y-4">
                        <h4 className="font-medium flex items-center gap-2">
                          <Search className="h-4 w-4" />
                          Search Console
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <p className="text-xs text-muted-foreground">Avg Position</p>
                            <div className="flex items-center gap-2">
                              <p className="text-xl font-bold">{googleMetrics.gsc.avgPosition.toFixed(1)}</p>
                              {googleMetrics.gsc.positionTrend === 'improving' && (
                                <TrendingUp className="h-4 w-4 text-green-500" />
                              )}
                              {googleMetrics.gsc.positionTrend === 'declining' && (
                                <TrendingDown className="h-4 w-4 text-red-500" />
                              )}
                            </div>
                          </div>
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <p className="text-xs text-muted-foreground">CTR</p>
                            <p className="text-xl font-bold">{(googleMetrics.gsc.ctr * 100).toFixed(1)}%</p>
                          </div>
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <p className="text-xs text-muted-foreground">Impressions</p>
                            <p className="text-xl font-bold">{googleMetrics.gsc.impressions.toLocaleString()}</p>
                          </div>
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <p className="text-xs text-muted-foreground">Clicks</p>
                            <p className="text-xl font-bold">{googleMetrics.gsc.clicks.toLocaleString()}</p>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Data: {googleMetrics.gsc.dateRange.start} to {googleMetrics.gsc.dateRange.end}
                        </p>
                      </div>
                    )}

                    {/* GA4 Metrics */}
                    {googleMetrics.ga4 && (
                      <div className="space-y-4">
                        <h4 className="font-medium flex items-center gap-2">
                          <BarChart3 className="h-4 w-4" />
                          Analytics
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 bg-green-50 rounded-lg">
                            <p className="text-xs text-muted-foreground">Sessions</p>
                            <p className="text-xl font-bold">{googleMetrics.ga4.sessions.toLocaleString()}</p>
                          </div>
                          <div className="p-3 bg-green-50 rounded-lg">
                            <p className="text-xs text-muted-foreground">Bounce Rate</p>
                            <p className="text-xl font-bold">{googleMetrics.ga4.bounceRate.toFixed(1)}%</p>
                          </div>
                          <div className="p-3 bg-green-50 rounded-lg">
                            <p className="text-xs text-muted-foreground">Avg Engagement</p>
                            <p className="text-xl font-bold">{Math.round(googleMetrics.ga4.avgEngagementTime)}s</p>
                          </div>
                          <div className="p-3 bg-green-50 rounded-lg">
                            <p className="text-xs text-muted-foreground">Conversions</p>
                            <p className="text-xl font-bold">{googleMetrics.ga4.conversions}</p>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Data: {googleMetrics.ga4.dateRange.start} to {googleMetrics.ga4.dateRange.end}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Google Before/After Comparison */}
                  {googleComparison && (
                    <div className="mt-6 pt-6 border-t">
                      <h4 className="font-medium mb-4">Content Deployment Impact (Google Data)</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {googleComparison.changes.positionChange !== null && (
                          <div className="p-3 rounded-lg border">
                            <p className="text-xs text-muted-foreground">Position Change</p>
                            <p className={`text-lg font-bold ${googleComparison.changes.positionChange < 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {googleComparison.changes.positionChange > 0 ? '+' : ''}{googleComparison.changes.positionChange.toFixed(1)}
                            </p>
                          </div>
                        )}
                        {googleComparison.changes.clickChange !== null && (
                          <div className="p-3 rounded-lg border">
                            <p className="text-xs text-muted-foreground">Click Change</p>
                            <p className={`text-lg font-bold ${googleComparison.changes.clickChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {googleComparison.changes.clickChange > 0 ? '+' : ''}{googleComparison.changes.clickChange.toFixed(0)}%
                            </p>
                          </div>
                        )}
                        {googleComparison.changes.bounceRateChange !== null && (
                          <div className="p-3 rounded-lg border">
                            <p className="text-xs text-muted-foreground">Bounce Rate Change</p>
                            <p className={`text-lg font-bold ${googleComparison.changes.bounceRateChange < 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {googleComparison.changes.bounceRateChange > 0 ? '+' : ''}{googleComparison.changes.bounceRateChange.toFixed(1)}%
                            </p>
                          </div>
                        )}
                        {googleComparison.changes.engagementTimeChange !== null && (
                          <div className="p-3 rounded-lg border">
                            <p className="text-xs text-muted-foreground">Engagement Time</p>
                            <p className={`text-lg font-bold ${googleComparison.changes.engagementTimeChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {googleComparison.changes.engagementTimeChange > 0 ? '+' : ''}{googleComparison.changes.engagementTimeChange.toFixed(0)}s
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* LLM Evaluation */}
            {pageData.evaluations && pageData.evaluations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>AI Content Evaluation</CardTitle>
                  <CardDescription>
                    How AI models perceive this page's content quality
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Radar Chart */}
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart
                          data={[
                            { metric: 'Clarity', value: parseFloat(pageData.evaluations[0]?.clarity_score || '0') },
                            { metric: 'Completeness', value: parseFloat(pageData.evaluations[0]?.completeness_score || '0') },
                            { metric: 'Trust', value: parseFloat(pageData.evaluations[0]?.trust_score || '0') },
                            { metric: 'Purchase Intent', value: parseFloat(pageData.evaluations[0]?.purchase_intent_score || '0') },
                            { metric: 'Technical Depth', value: parseFloat(pageData.evaluations[0]?.technical_depth_score || '0') },
                            { metric: 'Differentiation', value: parseFloat(pageData.evaluations[0]?.differentiation_score || '0') }
                          ]}
                        >
                          <PolarGrid />
                          <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12 }} />
                          <PolarRadiusAxis domain={[0, 10]} />
                          <Radar
                            name="Score"
                            dataKey="value"
                            stroke="#3b82f6"
                            fill="#3b82f6"
                            fillOpacity={0.5}
                          />
                          <Tooltip />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Score Details */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold">Overall Score</span>
                        <span className="text-3xl font-bold text-blue-600">
                          {pageData.evaluations[0]?.overall_score || '-'}/10
                        </span>
                      </div>
                      <Separator />
                      <div>
                        <p className="font-medium mb-2">Summary</p>
                        <p className="text-sm text-muted-foreground">
                          {pageData.evaluations[0]?.summary}
                        </p>
                      </div>
                      <div>
                        <p className="font-medium mb-2">Unanswered Questions</p>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {pageData.evaluations[0]?.unanswered_questions?.slice(0, 3).map((q, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-orange-500">?</span>
                              {q}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Evaluated by {pageData.evaluations[0]?.llm_model} ({pageData.evaluations[0]?.llm_provider})
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Keyword Value Table */}
            {pageData.keywordValue?.keywords && pageData.keywordValue.keywords.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Keyword Value Tracking</CardTitle>
                  <CardDescription>
                    Position changes and their monetary impact
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Keyword</TableHead>
                        <TableHead className="text-center">Position</TableHead>
                        <TableHead className="text-center">Source</TableHead>
                        <TableHead className="text-right">Search Vol</TableHead>
                        <TableHead className="text-right">CPC</TableHead>
                        <TableHead className="text-right">Traffic</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pageData.keywordValue.keywords.map((kw) => (
                        <TableRow key={kw.id}>
                          <TableCell className="font-medium">{kw.keyword}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <span className="text-muted-foreground">{kw.position_before}</span>
                              <ArrowUp className="h-3 w-3 text-green-500" />
                              <span className="font-bold">{kw.position_current}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {kw.metadata?.position_source === 'gsc' ? (
                              <Badge variant="default" className="bg-blue-500 text-xs">GSC</Badge>
                            ) : kw.metadata?.position_source === 'serp' ? (
                              <Badge variant="secondary" className="text-xs">SERP</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">Est.</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {kw.search_volume_monthly.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(kw.cpc_eur)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-muted-foreground">{kw.traffic_before}</span>
                              <ArrowUp className="h-3 w-3 text-green-500" />
                              <span className="font-bold">{kw.traffic_after}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={`font-medium ${
                              parseFloat(kw.value_delta_eur) > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {parseFloat(kw.value_delta_eur) > 0 ? '+' : ''}{formatCurrency(kw.value_delta_eur)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="flex justify-end mt-4 pt-4 border-t">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Total Monthly Value</p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(pageData.keywordValue.total)}/mo
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Page Timeline */}
            {pageData.timeline && pageData.timeline.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Page Timeline</CardTitle>
                  <CardDescription>
                    Events specific to this page
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pageData.timeline.map((event) => (
                      <div
                        key={event.id}
                        className="flex items-center gap-4 p-2 rounded-lg border"
                      >
                        {getEventIcon(event.event_type)}
                        <div className="flex-1">
                          <Badge className={getEventBadgeColor(event.event_type)} variant="outline">
                            {event.event_type.replace('_', ' ')}
                          </Badge>
                          {event.event_data.keyword && (
                            <span className="ml-2 text-sm">
                              "{event.event_data.keyword}" {event.event_data.oldPosition} → {event.event_data.newPosition}
                            </span>
                          )}
                        </div>
                        {event.impact_value_eur && parseFloat(event.impact_value_eur) !== 0 && (
                          <span className={`font-medium ${
                            parseFloat(event.impact_value_eur) > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatCurrency(event.impact_value_eur)}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {new Date(event.event_timestamp).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
